import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { registerUser, requestPasswordRecovery, userLogin, UserProfile } from '../api/api';
import { useApp } from '../context/AppContext';
import Button from '../components/Button';
import Card from '../components/Card';
import Input from '../components/Input';
import { clientErrorMessage } from '../utils/clientError';

function isProfileReady(user: UserProfile): boolean {
  return Boolean(
    user.lastName?.trim() &&
    user.firstName?.trim() &&
    user.birthDate?.trim() &&
    user.phone?.trim()
  );
}

function isKycReady(user: UserProfile): boolean {
  return user.isSbpVerified || user.kycStatus === 'Approved';
}

export function RegisterPage() {
  const navigate = useNavigate();
  const { userAuth } = useApp();
  const registrationCompletedRef = useRef(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (userAuth.token && !registrationCompletedRef.current) navigate('/', { replace: true });
  }, [navigate, userAuth.token]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await registerUser(email, password);
      registrationCompletedRef.current = true;
      userAuth.login(res.token);
      userAuth.setUser(res.user);
      navigate('/profile', { replace: true });
    } catch (err: any) {
      setError(clientErrorMessage(err, 'Не удалось зарегистрироваться. Повторите попытку позже.'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="admin-layout" style={{ maxWidth: 420, margin: '40px auto' }}>
      <Card>
        <h2 style={{ marginBottom: 20 }}>Регистрация</h2>
        <form onSubmit={submit} noValidate>
          <Input label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
          <Input
            label="Пароль"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            hint="Минимум 8 символов"
            minLength={8}
            required
          />
          {error && <div style={{ color: 'var(--danger)', margin: '10px 0' }}>{error}</div>}
          <Button type="submit" fullWidth loading={loading} disabled={!email || password.length < 8}>
            Создать аккаунт
          </Button>
        </form>
        <div style={{ marginTop: 16, textAlign: 'center' }}><Link to="/login" className="back-link">Уже есть аккаунт</Link></div>
      </Card>
    </div>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const { userAuth } = useApp();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoverySent, setRecoverySent] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await userLogin(email, password);
      userAuth.login(res.token);
      userAuth.setUser(res.user);
      navigate(isProfileReady(res.user) && isKycReady(res.user) ? '/' : '/profile', { replace: true });
    } catch {
      setError('Неверный email или пароль');
    } finally {
      setLoading(false);
    }
  }

  async function recoverPassword(e: React.FormEvent) {
    e.preventDefault();
    setRecoveryLoading(true);
    setError('');
    try {
      await requestPasswordRecovery(email);
      setRecoverySent(true);
    } catch {
      setError('Не удалось восстановить пароль. Повторите попытку позже.');
    } finally {
      setRecoveryLoading(false);
    }
  }

  return (
    <div className="admin-layout" style={{ maxWidth: 420, margin: '40px auto' }}>
      <Card>
        <h2 style={{ marginBottom: 20 }}>{recoveryOpen ? 'Восстановление пароля' : 'Вход'}</h2>
        <form onSubmit={recoveryOpen ? recoverPassword : submit} noValidate>
          <Input label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
          {!recoveryOpen && <Input label="Пароль" type="password" value={password} onChange={e => setPassword(e.target.value)} required />}
          {error && <div style={{ color: 'var(--danger)', margin: '10px 0' }}>{error}</div>}
          {recoverySent && (
            <div style={{ color: 'var(--success)', margin: '10px 0' }}>
              Если аккаунт с такой почтой существует, новый пароль отправлен на email.
            </div>
          )}
          <Button type="submit" fullWidth loading={recoveryOpen ? recoveryLoading : loading} disabled={recoveryOpen && (!email || recoverySent)}>
            {recoveryOpen ? 'Отправить новый пароль' : 'Войти'}
          </Button>
        </form>
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <button
            type="button"
            className="back-link"
            style={{ border: 0, background: 'none', cursor: 'pointer' }}
            onClick={() => {
              setRecoveryOpen(value => !value);
              setRecoverySent(false);
              setError('');
            }}
          >
            {recoveryOpen ? 'Вернуться ко входу' : 'Забыли пароль?'}
          </button>
        </div>
        {!recoveryOpen && <div style={{ marginTop: 12, textAlign: 'center' }}><Link to="/register" className="back-link">Регистрация</Link></div>}
      </Card>
    </div>
  );
}
